import isEmpty from 'lodash/isEmpty'
import { useEffect, useReducer } from 'react'
import { Button, InformationModal } from 'utoniq-ui'

import {
  ApplyPreSaleLotteryInput,
  Fee,
  FirstComeFirstServedDetailForGuestFragment,
  FirstComeFirstServedDetailFragment,
  Item,
  Maybe,
  PaymentDetailFragment,
  PaymentMethod,
  PreSaleStatus,
} from '@/clients/utoniq-core/schema'
import { useForm, UseFormProps, useRouter, useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'
import { setPreference } from '@/core/native/storage'

import {
  ButtonCell,
  CautionTextCell,
  CommissionListCell,
  PaymentInfoCell,
  TicketListCell,
} from './components'

type FirstComeFirstServedDetailFormProps = {
  preSaleId: string
  preSale:
    | FirstComeFirstServedDetailFragment
    | FirstComeFirstServedDetailForGuestFragment
  paymentSource?: Maybe<PaymentDetailFragment>
  originFullNameValue?: string
  onSubmit: (
    values: ApplyPreSaleFirstComeFirstServedValue | undefined
  ) => Promise<void>
  onReadyPaymentMethod: (
    values: ApplyPreSaleFirstComeFirstServedValue | undefined
  ) => Promise<void>
  paymentFees?: Maybe<Fee[]>
  choices:
    | { itemId: string; skuId: string | undefined; count: number }
    | undefined
}

export type ApplyPreSaleFirstComeFirstServedValue = Omit<
  ApplyPreSaleLotteryInput,
  'choices' | 'purchaseCountChoices'
> & {
  choices: { itemId: string; skuId: string | undefined }[]
  purchaseCountChoices: { count: number }[]
  isFirstComeFirstServed: boolean
}

const schema: UseFormProps['schema'] = o => ({
  preSaleId: o.string(),
  paymentMethod: o.string(),
  fullName: o.string().when('paymentMethod', {
    is: PaymentMethod.Konbini,
    then: o.string().label('氏名'),
    otherwise: o.string().optional().allow(null),
  }),
  choices: o.array().items(
    o.object({
      itemId: o.string(),
      skuId: o.string().optional().allow(null),
    })
  ),
  purchaseCountChoices: o.array().items(
    o.object({
      count: o.number(),
    })
  ),
  isFirstComeFirstServed: o.boolean().optional(),
})

export const FirstComeFirstServedDetailForm: Organism<FirstComeFirstServedDetailFormProps> =
  ({
    preSaleId,
    preSale,
    paymentSource,
    originFullNameValue,
    paymentFees,
    onSubmit,
    onReadyPaymentMethod,
    choices,
  }) => {
    const { t } = useTranslation('firstComeFirstServedDetail')
    const { generatePath, routerPages } = useRouter()
    const {
      watch,
      setValue,
      handleSubmit,
      register,
      control,
      getValues,
      formState: { isSubmitting, errors },
    } = useForm<ApplyPreSaleFirstComeFirstServedValue>({
      schema,
      defaultValues: {
        ...defaultValues,
        preSaleId: preSale.id,
        // 予め選択された状態の場合（ストアから直接遷移）
        choices: choices
          ? [{ itemId: choices.itemId, skuId: choices.skuId }]
          : defaultValues.choices,
        purchaseCountChoices: choices
          ? [{ count: choices.count }]
          : defaultValues.purchaseCountChoices,
        paymentMethod:
          preSale.paymentMethods?.[0] ?? defaultValues.paymentMethod,
      },
    })
    // メンバーシップ限定モーダルの表示状態
    const [membershipModalOpen, toggleMembershipModalOpen] = useReducer(
      (state: boolean) => !state,
      false
    )

    const paymentMethodWatch = watch('paymentMethod')
    const isStopSalling = preSale.preSaleStatus !== PreSaleStatus.Accepting
    const isBeforeReceiption =
      preSale.preSaleStatus === PreSaleStatus.BeforeReceiption

    // 離脱時に入力値を保存
    useEffect(() => {
      return () => {
        ;(async () => {
          await setPreference('preSaleLotteryApplicationInput', watch())
        })()
      }
    }, [])

    return (
      <OrganismFrame>
        {(preSale.preSaleStatus === PreSaleStatus.Accepting ||
          isBeforeReceiption ||
          preSale.preSaleStatus === PreSaleStatus.SoldOut) && (
          <TicketListCell
            preSaleId={preSaleId}
            items={preSale.items as Partial<Item>[]}
            control={control}
            errors={errors}
            maxChoice={1}
            preSaleStatus={preSale.preSaleStatus as PreSaleStatus}
            maxSelectItem={preSale.firstComeFirstServed?.purchasableCount}
            isBeforeReceiption={isBeforeReceiption}
            toggleMembershipModalOpen={toggleMembershipModalOpen}
            isAccessible={preSale.isAccessible}
          />
        )}
        {!isStopSalling && (
          <PaymentInfoCell
            paymentMethods={
              preSale.paymentMethods ?? [
                PaymentMethod.CreditCard,
                PaymentMethod.Konbini,
              ]
            }
            paymentFees={paymentFees}
            value={paymentMethodWatch}
            setValue={setValue}
            paymentSource={paymentSource}
            toggleMembershipModalOpen={toggleMembershipModalOpen}
            isAccessible={preSale.isAccessible}
            originFullNameValue={originFullNameValue ?? ''}
            inputFullNameProps={{
              ...register('fullName'),
              error: errors.fullName,
            }}
            onRegister={async () => {
              setTimeout(() => {
                onReadyPaymentMethod(getValues())
              }, 300)
            }}
            error={errors.paymentMethod?.message}
          />
        )}
        <CommissionListCell
          paymentFees={paymentFees}
          isKonbini={watch('paymentMethod') === PaymentMethod.Konbini}
        />
        <CautionTextCell note={preSale.note} />
        {!isStopSalling && (
          <ButtonCell
            onSubmit={handleSubmit(v => {
              onSubmit(v)
            })}
            disabled={
              isSubmitting ||
              (watch('paymentMethod') === PaymentMethod.CreditCard &&
                !paymentSource) ||
              !isEmpty(errors) ||
              !watch('choices')?.filter(({ itemId }) => itemId).length
            }
            isSubmitting={isSubmitting}
          />
        )}
        <InformationModal
          open={membershipModalOpen}
          onClose={toggleMembershipModalOpen}
          title={t('membershipRequired', 'メンバーシップへの加入が必要です')}
          content={<></>}
          buttons={
            <Button
              size="large"
              color="primary"
              sx={{ minWidth: 212 }}
              href={generatePath(routerPages.ArtistMembership, {
                artistId: preSale?.artist?.id ?? '#',
              })}
              onClick={() => {
                /**
                 * TODO:
                 * 年間メンバーシッププラン機能に伴う導線実装が完了したら、先行販売に戻るUIを下記ローカルストレージキーをもとに実装すること
                 * https://zpl.io/kKOkROg
                 */
                setPreference('currentPreSaleId', {
                  preSaleId: preSale.id,
                })
              }}
            >
              {t('seeDetail')}
            </Button>
          }
        />
      </OrganismFrame>
    )
  }

const defaultValues: ApplyPreSaleFirstComeFirstServedValue = {
  choices: [{ itemId: '', skuId: undefined }],
  fullName: null,
  paymentMethod: PaymentMethod.CreditCard,
  preSaleId: '',
  purchaseCountChoices: [{ count: 1 }],
  isFirstComeFirstServed: true,
}
